import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaBriefcase, FaMapMarkerAlt } from 'react-icons/fa';
import auspifyOffer from '../assets/auspify-offer-letter.jpg';
import codealphaOffer from '../assets/codealpha-offer-letter.jpg';
import nextgenOffer from '../assets/nextgen-offer-letter.jpg';

const experiences = [
  {
    company: 'CodeAlpha',
    role: 'Application Developer Intern',
    dates: 'September 2026 - Present',
    location: 'Karachi Central District',
    image: codealphaOffer,
    summary: 'Developing user-friendly applications with an emphasis on innovation and engagement.',
    highlights: ['Created a FlashCard Quiz App and Random Quote Generator.', 'Used Flutter shared preferences for improved data retention.', 'Designed minimal UI/UX layouts.'],
  },
  {
    company: 'Next Gen Software Hub Pvt. Ltd.',
    role: 'Web Developer Intern',
    dates: 'September 2026 - Present',
    location: 'Karachi Central District',
    image: nextgenOffer,
    summary: 'Worked as a Flutter Web Developer Intern building responsive and interactive applications with Flutter and Dart.',
    highlights: ['Built reusable interfaces, navigation, state handling, and animations.', 'Integrated REST APIs with HTTP, JSON, Future, and async/await.', 'Used Git and GitHub for version control and project tracking.', 'Built portfolio, quiz/calculator, weather, and business Flutter web applications.'],
  },
  {
    company: 'Auspify Technologies',
    role: 'Flutter Developer Intern',
    dates: 'September 2026 - Present',
    location: 'Karachi Central District',
    image: auspifyOffer,
    summary: 'Developed responsive Flutter applications for Android and iOS with a focus on clean UI/UX and modular architecture.',
    highlights: ['Developed four responsive Flutter applications for Android and iOS.', 'Integrated REST APIs for live weather data.', 'Created a personal finance Expense Tracker App.', 'Maintained modular architecture and code quality.'],
  },
];

export default function Experience() {
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(() => {
    if (!selectedOffer) return undefined;
    const closeOnEscape = event => event.key === 'Escape' && setSelectedOffer(null);
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [selectedOffer]);

  return (
    <section className="px-4 py-16 sm:px-6 sm:py-24 lg:px-8" id="experience">
      <div className="site-container">
        <div className="mb-8 max-w-2xl sm:mb-12">
          <p className="section-eyebrow">Experience</p>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-5xl">Professional journey.</h2>
        </div>
        <div className="relative space-y-5 before:absolute before:bottom-5 before:left-5 before:top-5 before:w-px before:bg-slate-800 sm:space-y-6">
          {experiences.map(experience => (
            <article key={`${experience.company}-${experience.role}`} className="surface-card relative ml-0 p-5 pl-14 sm:p-7 sm:pl-16">
              <span className="absolute left-2.5 top-6 flex h-5 w-5 items-center justify-center rounded-full border-4 border-[#070a0f] bg-cyan-300" aria-hidden="true" />
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-cyan-300">{experience.dates}</p>
                  <h3 className="mt-2 text-xl font-bold text-white">{experience.role}</h3>
                  <p className="mt-1 text-sm font-semibold text-slate-300">{experience.company}</p>
                </div>
                <p className="inline-flex items-center gap-2 text-xs text-slate-500"><FaMapMarkerAlt aria-hidden="true" />{experience.location}</p>
              </div>
              <p className="mt-5 text-sm leading-7 text-slate-400">{experience.summary}</p>
              <ul className="mt-5 space-y-2 text-sm leading-6 text-slate-300">
                {experience.highlights.map(highlight => <li key={highlight} className="flex gap-3"><span className="text-cyan-300">/</span><span>{highlight}</span></li>)}
              </ul>
              <button type="button" onClick={() => setSelectedOffer(experience)} className="focus-ring mt-6 block max-w-xs overflow-hidden rounded-xl border border-slate-700 bg-slate-950 text-left" aria-label={`Preview ${experience.company} offer letter`}>
                <img src={experience.image} alt={`${experience.company} internship offer letter`} className="block h-60 w-full object-contain transition duration-300 hover:scale-[1.02]" />
                <span className="block border-t border-slate-800 px-3 py-2 text-xs font-semibold text-slate-400">View offer letter</span>
              </button>
            </article>
          ))}
        </div>
      </div>
      {selectedOffer && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-sm" role="presentation" onMouseDown={event => event.target === event.currentTarget && setSelectedOffer(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="offer-preview-title" className="relative max-h-[92vh] w-full max-w-4xl overflow-auto rounded-2xl border border-slate-700 bg-[#0d1117] p-3 shadow-2xl">
            <div className="flex items-center justify-between gap-4 p-3">
              <h2 id="offer-preview-title" className="text-sm font-bold text-white">{selectedOffer.company} offer letter</h2>
              <button type="button" onClick={() => setSelectedOffer(null)} className="focus-ring rounded-full border border-slate-700 px-3 py-1.5 text-xs font-bold text-slate-300 hover:text-white" aria-label="Close offer letter preview">Close</button>
            </div>
            <img src={selectedOffer.image} alt={`${selectedOffer.company} internship offer letter full preview`} className="mx-auto max-h-[78vh] w-full object-contain" />
          </div>
        </div>
        , document.body
      )}
    </section>
  );
}
